import React, { useEffect, useMemo, useState } from "react";
import { Button, Card, Drawer, Space, Table, Tag, Typography } from "antd";
import AdminOrdersService from "../../../Modules/Services/admin.orders.service";

const { Text } = Typography;

const formatRub = (value) =>
  new Intl.NumberFormat("ru-RU", {
    style: "currency",
    currency: "RUB",
    maximumFractionDigits: 2,
  }).format(Number(value || 0));

const toDateAssumingYakutsk = (value) => {
  if (value instanceof Date) return value;
  if (typeof value === "string") {
    const s = value.trim();
    const m = s.match(/^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2}:\d{2})(\.\d+)?$/);
    if (m) return new Date(`${m[1]}T${m[2]}${m[3] || ""}+09:00`);
    return new Date(s);
  }
  return new Date(value);
};

const formatDateTime = (v) => {
  try {
    const d = toDateAssumingYakutsk(v);
    if (Number.isNaN(d.getTime())) return String(v);
    return new Intl.DateTimeFormat("ru-RU", {
      timeZone: "Asia/Yakutsk",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    }).format(d);
  } catch {
    return String(v);
  }
};

const ManageOrders = () => {
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [rows, setRows] = useState([]);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);

  const fetchList = async () => {
    try {
      setLoading(true);
      const res = await AdminOrdersService.getOrders(page, pageSize);
      if (res?.success) {
        setRows(res.data.items || []);
        setTotal(Number(res.data.total || 0));
      }
    } catch (e) {
      console.error("Failed to fetch orders:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList();
  }, [page, pageSize]);

  const openOrder = async (id) => {
    try {
      setDrawerOpen(true);
      setSelectedOrder(null);
      setDetailsLoading(true);
      const res = await AdminOrdersService.getOrderById(id);
      if (res?.success) setSelectedOrder(res.data);
    } catch (e) {
      console.error("Failed to fetch order:", e);
    } finally {
      setDetailsLoading(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        title: "№",
        dataIndex: "id",
        width: 80,
      },
      {
        title: "Тип",
        dataIndex: "order_type",
        width: 120,
        render: (t) =>
          t === "delivery" ? (
            <Tag color="blue">Доставка</Tag>
          ) : (
            <Tag>Самовывоз</Tag>
          ),
      },
      {
        title: "Телефон",
        dataIndex: "phone",
        width: 160,
        render: (v) => <Text copyable>{v}</Text>,
      },
      {
        title: "Адрес",
        dataIndex: "address",
        ellipsis: true,
        render: (v, r) => (r.order_type === "delivery" ? v || "—" : "—"),
      },
      {
        title: "Товары",
        dataIndex: "total_items",
        width: 110,
      },
      {
        title: "Сумма товаров",
        dataIndex: "total_price",
        width: 140,
        render: (v) => formatRub(v),
      },
      {
        title: "Доставка",
        dataIndex: "delivery_fee",
        width: 120,
        render: (v, r) => (r.order_type === "delivery" ? formatRub(v) : "—"),
      },
      {
        title: "Итого",
        dataIndex: "total_sum",
        width: 130,
        render: (v) => formatRub(v),
      },
      {
        title: "Создан",
        dataIndex: "created_at",
        width: 170,
        render: (v) => formatDateTime(v),
      },
      {
        title: "",
        key: "actions",
        width: 120,
        render: (_, r) => (
          <Button size="small" onClick={() => openOrder(r.id)}>
            Открыть
          </Button>
        ),
      },
    ],
    []
  );

  return (
    <div>
      <h1>Заказы</h1>
      <Card>
        <Table
          rowKey="id"
          loading={loading}
          columns={columns}
          dataSource={rows}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            pageSizeOptions: [10, 20, 50, 100],
            onChange: (p, ps) => {
              setPage(p);
              setPageSize(ps);
            },
          }}
        />
      </Card>

      <Drawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        width={520}
        title={selectedOrder ? `Заказ #${selectedOrder.id}` : "Заказ"}
      >
        {detailsLoading && <div>Загрузка...</div>}
        {!detailsLoading && selectedOrder && (
          <Space direction="vertical" style={{ width: "100%" }} size={12}>
            <div>
              Тип:{" "}
              {selectedOrder.order_type === "delivery"
                ? "Доставка"
                : "Самовывоз"}
            </div>
            <div>
              Телефон: <Text copyable>{selectedOrder.phone}</Text>
            </div>
            {selectedOrder.order_type === "delivery" && (
              <div>Адрес: {selectedOrder.address || "—"}</div>
            )}
            <div>Создан: {formatDateTime(selectedOrder.created_at)}</div>
            <div>
              Товары: <b>{formatRub(selectedOrder.total_price)}</b>
            </div>
            {selectedOrder.order_type === "delivery" && (
              <div>
                Доставка: <b>{formatRub(selectedOrder.delivery_fee)}</b>
              </div>
            )}
            <div>
              Итого: <b>{formatRub(selectedOrder.total_sum)}</b>
            </div>

            <Card size="small" title="Состав заказа">
              <Space direction="vertical" style={{ width: "100%" }} size={8}>
                {(selectedOrder.items || []).map((it) => {
                  const option =
                    it.size || it.weight ? ` (${it.size || it.weight})` : "";
                  const sum = Number(it.price) * Number(it.quantity);
                  return (
                    <div key={it.id}>
                      <div style={{ fontWeight: 700 }}>
                        {it.product_name}
                        {option}
                      </div>
                      <div style={{ color: "rgba(0,0,0,0.65)" }}>
                        {it.quantity} × {formatRub(it.price)} ={" "}
                        <b>{formatRub(sum)}</b>
                      </div>
                    </div>
                  );
                })}
              </Space>
            </Card>
          </Space>
        )}
      </Drawer>
    </div>
  );
};

export default ManageOrders;
